import * as React from 'react'
import { ReactNode, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, makeStyles, mergeClasses, Text, tokens } from '@fluentui/react-components'
import { ErrorCircleRegular } from '@fluentui/react-icons'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import SubmittingButton from '../../../components/Button/SubmittingButton'

export type PartySection = {
    key: string
    labelKey: string
    content: ReactNode
    /**
     * Field names whose errors show up in this section. A collapsible section is only expanded on a
     * failed submit when it actually holds one of the errors, and an always-open section names its
     * fields so an error there does not count as homeless. Leave an error unclaimed by every section
     * and the accordion opens completely, so a mistake can never end up hidden behind a closed
     * header.
     */
    fieldNames?: string[]
}

type Props = {
    goBack: () => void
    /** Always-open sections (name, contact, address). */
    mainSections: PartySection[]
    /** Collapsible sections (already filtered by active module). */
    extraSections: PartySection[]
    /** Extra content rendered inside the accordion, after the sections (e.g. dimension AccordionItems). */
    accordionExtra?: ReactNode
}

const useStyles = makeStyles({
    root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
    header: {
        position: 'sticky',
        top: 'var(--sticky-top)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: tokens.spacingHorizontalS,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        backgroundColor: tokens.colorNeutralBackground1,
        borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    },
    // The error counter sits on the left, the actions stay right-aligned.
    errorIndicator: { color: tokens.colorPaletteRedForeground1, flexShrink: 0, marginRight: 'auto' },
    grid: { display: 'grid', gap: tokens.spacingHorizontalL, alignItems: 'start' },
    gridWide: { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' },
    gridNarrow: { gridTemplateColumns: '1fr' },
    column: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, minWidth: 0 },
    section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
    sectionBody: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
})

/** Top-level field names that currently carry an error, e.g. { name: { last } } -> ['name']. */
const errorFieldNames = (errors: object): string[] => Object.keys(errors ?? {})

// Flatten Formik's nested error object to a flat count (mirrors Form.tsx's flatMapError).
const countErrors = (error: unknown): number => {
    if (!error || error instanceof Function) {
        return 0
    }
    if (Array.isArray(error)) {
        return error.reduce((sum, e) => sum + countErrors(e), 0)
    }
    if (typeof error === 'object') {
        return Object.values(error).reduce((sum: number, e) => sum + countErrors(e), 0)
    }
    return 1
}

/**
 * Shell for the party add/edit screens (HAM-3911): a sticky action header (validation indicator +
 * Save/Cancel), the always-open sections, and a collapsible accordion for the rest. From
 * the XXL breakpoint the layout splits into two columns to use the wide/standalone space (AC9).
 *
 * When a submit produces validation errors, every collapsible section is expanded and the first
 * invalid field is scrolled into view, so no error stays hidden off-screen (AC4).
 */
export default function PartyFormLayout(props: Props): ReactElement {
    const { t: translate } = useTranslation()
    const { errors, submitCount, submitForm, isSubmitting } = useFormikContext()
    const styles = useStyles()
    const { isViewport, viewport } = useViewport()
    const wide = useMemo(() => isViewport('>=', Viewport.XXL), [viewport])
    const rootRef = useRef<HTMLDivElement>(null)

    const errorCount = useMemo(() => countErrors(errors), [errors])
    const [openItems, setOpenItems] = useState<string[]>([])
    /** Submit attempt the sections were last expanded for, so one attempt never expands them twice. */
    const expandedFor = useRef<number>(0)

    const scrollToFirstError = () =>
        rootRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' })

    // On a failed submit, reveal the sections holding an error and jump to the first invalid field.
    // Formik raises submitCount before validation has produced the errors, so this watches both and
    // acts on the first render where the attempt has errors to show. Doing it once per attempt is
    // the point: the errors change again the moment the user fixes a field, and re-expanding the
    // screen while they are typing throws away how they just arranged it.
    useEffect(() => {
        if (submitCount === 0 || errorCount === 0 || expandedFor.current === submitCount) {
            return undefined
        }
        expandedFor.current = submitCount

        const failed = errorFieldNames(errors)
        const claimed = [...props.mainSections, ...props.extraSections].flatMap(section => section.fieldNames ?? [])
        // An error no section claims may sit anywhere - in the dimension items, or in a section that
        // doesn't name its fields - so then everything opens rather than leaving it hidden.
        const unclaimed = failed.some(field => !claimed.includes(field))
        const guilty = props.extraSections.filter(section => section.fieldNames?.some(field => failed.includes(field)))
        setOpenItems((unclaimed ? props.extraSections : guilty).map(section => section.key))

        const timer = setTimeout(scrollToFirstError, 0)
        return () => clearTimeout(timer)
    }, [submitCount, errors])

    return (
        <div ref={rootRef} className={styles.root}>
            <div className={styles.header}>
                {errorCount > 0 && (
                    <Button
                        type="button"
                        appearance="subtle"
                        size="small"
                        icon={<ErrorCircleRegular />}
                        className={styles.errorIndicator}
                        onClick={scrollToFirstError}
                    >
                        {translate('party.edit.validation.count', { count: errorCount })}
                    </Button>
                )}
                <Button type="button" appearance="secondary" onClick={props.goBack}>
                    {translate('action.cancel')}
                </Button>
                <SubmittingButton type="button" appearance="primary" isSubmitting={isSubmitting} onClick={submitForm}>
                    {translate('action.save')}
                </SubmittingButton>
            </div>

            <div className={mergeClasses(styles.grid, wide ? styles.gridWide : styles.gridNarrow)}>
                <div className={styles.column}>
                    {props.mainSections.map(section => (
                        <div key={section.key} className={styles.section}>
                            <Text size={400} weight="semibold">
                                {translate(section.labelKey)}
                            </Text>
                            <div className={styles.sectionBody}>{section.content}</div>
                        </div>
                    ))}
                </div>
                <div className={styles.column}>
                    <Accordion multiple collapsible openItems={openItems} onToggle={(_, data) => setOpenItems(data.openItems as string[])}>
                        {props.extraSections.map(section => (
                            <AccordionItem key={section.key} value={section.key}>
                                <AccordionHeader>{translate(section.labelKey)}</AccordionHeader>
                                <AccordionPanel className={styles.sectionBody}>{section.content}</AccordionPanel>
                            </AccordionItem>
                        ))}
                        {props.accordionExtra}
                    </Accordion>
                </div>
            </div>
        </div>
    )
}
